import { useRouter } from 'next/router';
import { t } from 'i18next';
import useLocales from '@/hooks/useLocales';
import { useModals } from '@mattjennings/react-modal-stack';
import { AlertDialog, ConfirmDialog } from '@/components/dialogs';
import { useSelector, useDispatch } from 'react-redux';
import useStepper from './checkout/useStepper';
import useErrorTracking from '@/hooks/userTracking/useErrorTracking';
import { resetSelectedAndClean } from '@/store/msci/msci.slice';

const errorCodes = [
  'LINK_INACTIVE',
  'LINK_EXPIRED',
  'LINK_USED',
  'LINK_NOT_AVAILABLE',
  'BOOKING_EXPIRED',
];

// generate-payment errors the user can recover from by reloading the page
// (BOL-256). PAYMENT_AMOUNT_MISMATCH additionally clears the selected MSI
// plan so summary and addon re-quote on a fresh payment plan (BOL-246).
const paymentRetryCodes = [
  'PAYMENT_AMOUNT_MISMATCH',
  'BOOKING_OXXO_AMOUNT_INVALID',
];

// The OXXO/cash paths fire confirmBackCode twice for the same error
// (useCallApiProcess fires it internally and useCheckoutPayBooking fires
// it again with the returned error), stacking two identical dialogs; the
// close icon only pops one layer, exposing the duplicate before the
// reload. Dedupe with a short time window instead of a boolean latch so
// a dismissal that never reaches the reload cb (programmatic closeModal,
// a throw before the modal opens) cannot suppress future retry modals.
let paymentRetryModalShownAt = 0;
const PAYMENT_RETRY_DEDUPE_MS = 2000;

// Message shown when the backend reports a failure without an error code
// (TECH-441). bookings-core answers HTTP 500 with an empty body for business
// outcomes such as "bank decline processed", so there is no code to translate
// and the user used to read "Ocurrió un error inesperado: (undefined)".
// 00007 ("Comunícate con tu banco o prueba con otro método de pago") already
// described the real cause, so it is reused instead of inventing a new one.
const NO_CODE_MESSAGE = '00007';

// Deliberately a separate counter from paymentRetryModalShownAt. Sharing it
// would let a coded retry and a no-code failure suppress each other: if the
// no-code modal swallowed a PAYMENT_AMOUNT_MISMATCH, its resetSelectedAndClean()
// would never run and the stale MSI plan could trigger the mismatch again after
// the reload.
let noCodeModalShownAt = 0;

// A backend error can reach the front without a code as undefined, null or an
// empty string depending on which branch of useCallApiProcess forwarded it.
const isMissingCode = (code) =>
  code === undefined || code === null || code === '';

export default function useDialogs() {
  const booking = useSelector((state) => state.booking);
  const { translate, getObjectOrMessage } = useLocales();
  const { openModal, closeModal } = useModals();

  const router = useRouter();

  const dispatch = useDispatch();

  const { jumpTo } = useStepper();

  const { checkoutErrorShown } = useErrorTracking();

  const getMessageObj = (code) => {
    let message = getObjectOrMessage(`messages.${code}`);
    if (message) {
      if (typeof message !== 'object') {
        if (message && typeof message === 'string') {
          const objbase = getObjectOrMessage(`messages.default`);
          message = {
            ...objbase,
            body: message,
          };
        } else {
          message = translate(`messages.default`);
        }
      }
    } else if (isMissingCode(code)) {
      // No code at all: never render the raw value, it prints "(undefined)".
      message = getObjectOrMessage(`messages.${NO_CODE_MESSAGE}`);
    } else {
      // Code present but unknown to the front. It is still shown because it
      // helps support identify the case, unlike an empty/undefined value.
      const objbase = getObjectOrMessage(`messages.default`);
      message = {
        ...objbase,
        body: `${objbase.body}: (${code})`,
      };
    }

    return message;
  };

  const alert = (text, title = 'Lo sentimos.', acceptLabel = 'Entendido') => {
    openModal(AlertDialog, {
      title,
      text,
      acceptLabel,
    });
  };

  const alertLocale = (code) => {
    alert(translate(code));
  };

  const alertBackCode = (code) => {
    let message = getMessageObj(code);

    checkoutErrorShown({
      source: 'backend',
      code,
      message: message.body,
    });
    alert(message.body, message.title, message.button);
  };

  const confirmBackCode = (code, token) => {
    let message = getMessageObj(code);
    const path = router.pathname;

    if (paymentRetryCodes.includes(code) && path === '/payment') {
      const now = Date.now();
      if (now - paymentRetryModalShownAt < PAYMENT_RETRY_DEDUPE_MS) return;
      paymentRetryModalShownAt = now;

      const { title, text, acceptLabel } = t(`checkout.modals.${code}`, {
        returnObjects: true,
      });

      checkoutErrorShown({
        source: 'backend',
        code,
        message: text,
      });
      confirm({
        title,
        text,
        acceptLabel,
        token,
        cb: () => {
          if (code === 'PAYMENT_AMOUNT_MISMATCH') {
            dispatch(resetSelectedAndClean());
          }
          router.reload();
        },
      });
      return;
    }

    if (
      (errorCodes.includes(code) && path === '/payment') ||
      code === 'INVALID_SESSION_TOKEN'
    ) {
      const localesPath = `checkout.modals.${code}`;
      const { title, text, acceptLabel } = t(localesPath, {
        returnObjects: true,
      });

      checkoutErrorShown({
        source: 'backend',
        code,
        message: text,
      });
      confirm({
        title,
        text,
        acceptLabel,
        token,
        cb: () => {
          jumpTo('HOME');
        },
      });
      return;
    }
    // Payment failure with no code (TECH-441). Same recovery as the coded
    // retryable errors above: reload so the payment provider scripts are
    // fetched again, in case the SDK itself failed to load (useSpreedly.js
    // already guards for a missing window.Spreedly). A soft navigation would
    // not re-request them.
    //
    // The card fields are lost on reload — they live in react-hook-form, not
    // in Redux — but the booking, tickets, promotion and timer survive via
    // reHydrateStore, so the user only re-enters the card.
    if (isMissingCode(code) && path === '/payment') {
      const now = Date.now();
      if (now - noCodeModalShownAt < PAYMENT_RETRY_DEDUPE_MS) return;
      noCodeModalShownAt = now;

      checkoutErrorShown({
        source: 'backend',
        code,
        message: message.body,
      });
      confirm({
        text: message.body,
        title: message.title,
        acceptLabel: message.button,
        token,
        cb: () => {
          router.reload();
        },
      });
      return;
    }

    checkoutErrorShown({
      source: 'backend',
      code,
      message: message.body,
    });
    confirm({
      text: message.body,
      title: message.title,
      acceptLabel: message.button,
      token,
    });
  };

  const confirm = ({
    title,
    text,
    acceptLabel,
    token,
    cb = () => {},
    omitBooking = false,
  }) => {
    openModal(ConfirmDialog, {
      title,
      text,
      acceptLabel,
      token,
      booking: booking?.orderInfo?.number,
      onConfirm: async () => {
        closeModal();
        cb();
      },
      onClose: cb,
      omitBooking,
    });
  };

  return {
    openModal,
    closeModal,
    alert,
    alertLocale,
    alertBackCode,
    confirmBackCode,
    confirm,
  };
}
